import React from 'react'
import { useTranslation } from 'react-i18next'
import Head, { PageHead } from '@fs/zion-head'
import { useLocation } from '@fs/zion-router'
import { rootstechSocialShare } from '../../../images'
import getPageTitle from './getPageTitle'

// Helper: Detect image MIME type from URL
function getImageType(imageUrl = '') {
  if (imageUrl.match(/\.png/gi)) return 'image/png'
  if (imageUrl.match(/\.gif/gi)) return 'image/gif'
  if (imageUrl.match(/\.svg/gi)) return 'image/svg+xml'
  return 'image/jpeg'
}

// Helper: Build SEO data from Brightspot data object with fallback hierarchy
function buildSeoData(data) {
  if (!data) return null

  return {
    ...data.seo,
    title: data?.seo?.title || data?.title || data?.name,
    description: data?.seo?.description || data?.description,
    openGraph: {
      ...data?.seo?.openGraph,
      title: data?.seo?.openGraph?.title || data?.seo?.title || data?.title || data?.name,
      description: data?.seo?.openGraph?.description || data?.seo?.description || data?.description,
    },
  }
}

const SeoPageHead = ({ seo, data }) => {
  const [t] = useTranslation()
  const { pathname } = useLocation()

  // Build SEO data from data prop or use seo prop directly
  const seoData = data ? buildSeoData(data) : seo

  if (!seoData) {
    return null
  }

  // Extract SEO data
  const { twitter, canonicalLink, title, description, openGraph } = seoData
  const clientTitle = getPageTitle(pathname, t)
  const seoTitle = title || clientTitle
  const seoDescription = description || ''
  const twitterUsername = twitter?.username !== '@FamilySearch' ? twitter?.username : '@RootsTechConf'
  const twitterImage = twitter?.image || {}
  const openGraphTitle = openGraph?.title || seoTitle
  const openGraphDescription = openGraph?.description || seoDescription
  const openGraphImage = openGraph?.image || {}
  const openGraphImageMetadata = openGraphImage?.metadata || {}
  const image = openGraphImage?.url || rootstechSocialShare

  return (
    <>
      <PageHead
        title={seoTitle}
        description={seoDescription}
        socialShareTitle={openGraphTitle}
        socialShareDescription={openGraphDescription}
        siteName={t('global.rootstech', 'RootsTech')}
        image={{
          alt: openGraphTitle,
          url: image,
          width: openGraphImageMetadata?.origWidthPixels || 1200, // 1200
          height: openGraphImageMetadata?.origHeightPixels || 630, // 630
        }}
        twitterSite={twitterUsername}
        twitterCreator={twitterUsername}
        twitterCard={twitterImage?.url ? 'summary_large_image' : 'summary'}
        omitSiteName
      />
      <Head>
        {seoData?.noIndexRobot && <meta name="robots" content="noindex" />}
        {seoData?.noFollowRobot && <meta name="robots" content="nofollow" />}
        {seoData?.noImageIndexRobot && <meta name="robots" content="noimageindex" />}
        {openGraphImage?.url && <meta property="og:image:type" content={getImageType(openGraphImage.url)} />}
        <link rel="canonical" href={canonicalLink || window?.location?.href} />
      </Head>
    </>
  )
}

export default SeoPageHead
